import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom React hook that provides countdown timer functionality with progress tracking.
 *
 * @param durationMs - The total duration of the countdown in milliseconds.
 * @returns An object containing:
 * - `remainingMs`: The number of milliseconds remaining in the countdown.
 * - `progressPercent`: The percentage of time remaining (0-100).
 * - `isDone`: Boolean indicating if the countdown has finished.
 * - `showRefresh`: Boolean indicating if the countdown is within the last 30 seconds.
 * - `reset`: Function to reset the countdown to the initial duration.
 *
 * @example
 * const { remainingMs, progressPercent, isDone, showRefresh, reset } = useCountdownProgress(60000);
 */
export function useCountdownProgress(durationMs: number) {
  const [remainingMs, setRemainingMs] = useState(durationMs);
  const [showRefresh, setShowRefresh] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const everySecond = 1000;

  const reset = useCallback(() => {
    setStartTime(Date.now());
    setRemainingMs(durationMs);
    setShowRefresh(false);
  }, [durationMs]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(durationMs - elapsed, 0);
      setRemainingMs(remaining);
      setShowRefresh(remaining < 40000);
      if (remaining === 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }, everySecond);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startTime, durationMs]);

  // eslint-disable-next-line no-magic-numbers
  const progressPercent = (remainingMs / durationMs) * 100;

  return {
    remainingMs,
    progressPercent,
    isDone: remainingMs === 0,
    showRefresh,
    reset,
  };
}
